import { observable, makeObservable, action, runInAction, computed } from "mobx";
import { RootStore } from "src/stores/RootStore";
import i18next from "i18next";
import { MessageType } from "src/components/notifications/Notifier";
import { apolloClientInstance } from "src/network/apolloClientInstance";
import { GET_ASSEMBLY_PROTOCOL_FILE, UPDATE_ASSEMBLY_PROTOCOL } from "src/api/assembly";
import { UpdateAssemblyProtocol, UpdateAssemblyProtocolVariables } from "src/api/generated/UpdateAssemblyProtocol";
import { GetAssemblyProtocolFile } from "src/api/generated/GetAssemblyProtocolFile";
import { NetworkConfig } from "src/network/NetworkConfig";
import { getRoleKey } from "src/network/User";
import { GetGenerateNewProtocolData } from "src/api/generated/GetGenerateNewProtocolData";
import { GET_GENERATE_NEW_PROTOCOL_DATA } from "src/api/protocol";
import { format } from "date-fns-tz";

export class ProtocolStore {
    rootStore: RootStore;
    initialAssemblyProtocol: { protocolauthor: string | null; protocoldate: string | null } | null = null;
    protocolFileId: string = "";
    protocolFileURL: string = "";
    isProtocolPdfLoading: boolean = true;
    assemblyDataHasChanged: boolean = false;
    isProtocolPDFDownloading: boolean = false;

    constructor(rootStore: RootStore) {
        this.rootStore = rootStore;

        makeObservable(this, {
            initialAssemblyProtocol: observable,
            protocolFileId: observable,
            isProtocolPDFDownloading: observable,
            protocolFileURL: observable,

            protocolHasChanged: computed,

            saveAssemblyProtocol: action,
            discardAssemblyProtocol: action,
            generateAssemblyProtocolPdf: action,
            downloadAssemblyProtocolPdf: action,
            checkIfAssemblyDataHasChanged: action,
            checkAndDownloadAssemblyProtocolPdf: action,
            getAssemblyProtocolFile: action,
            downloadAssemblyProtocolFile: action,

            setInitialProtocol: action,
            setProtocolFileId: action,
            setAssemblyDataHasChanged: action,
            setIsProtocolPDFDownloading: action,
            setProtocolFileURL: action,
            setIsProtocolPdfLoading: action
        });
    }

    get protocolHasChanged(): boolean {
        const current = this.rootStore.assemblyStore.currentAssembly;
        if (!current || !this.initialAssemblyProtocol) return false;
        return (
            current.protocolauthor !== this.initialAssemblyProtocol.protocolauthor ||
            current.protocoldate !== this.initialAssemblyProtocol.protocoldate
        );
    }

    saveAssemblyProtocol = async () => {
        try {
            const response = await apolloClientInstance.mutate<UpdateAssemblyProtocol, UpdateAssemblyProtocolVariables>(
                {
                    mutation: UPDATE_ASSEMBLY_PROTOCOL,
                    variables: {
                        assemblyid: this.rootStore.assemblyStore.currentAssembly?.id,
                        protocolauthor: this.rootStore.assemblyStore.currentAssembly?.protocolauthor ?? "",
                        protocoldate: this.rootStore.assemblyStore.currentAssembly?.protocoldate
                    }
                }
            );

            if (response?.data) {
                this.rootStore.uiStore.printStatusMessage(
                    i18next.t("screens.assemblies.protocolcontent.save_success"),
                    MessageType.SUCCESS
                );

                runInAction(() => {
                    const current = this.rootStore.assemblyStore.currentAssembly;
                    if (current) {
                        this.initialAssemblyProtocol = {
                            protocolauthor: current.protocolauthor,
                            protocoldate: current.protocoldate
                        };
                    }
                });
            } else {
                console.warn("Mutation succeeded but no data returned.");
            }
        } catch (error: any) {
            console.error("Error updating assembly protocol:", error.message);
            this.rootStore.uiStore.printStatusMessage(
                i18next.t("screens.assemblies.protocolcontent.save_error"),
                MessageType.ERROR
            );
        }
    };

    discardAssemblyProtocol = () => {
        const currentAssembly = this.rootStore.assemblyStore.currentAssembly;
        const initial = this.initialAssemblyProtocol;
        if (currentAssembly && initial) {
            runInAction(() => {
                currentAssembly.protocolauthor = initial.protocolauthor || "";
                currentAssembly.protocoldate = initial.protocoldate || "";
            });
        }
    };

    downloadAssemblyProtocolFile = async (fileId: string): Promise<any | undefined> => {
        let downloadedAssemblyProtocolFile = undefined;

        try {
            const accessToken = this.rootStore.authStore.token;
            const tokenType = this.rootStore.authStore.tokenType;
            const role = this.rootStore.authStore.user?.role;

            const downloadAssemblyProtocolFileResponse = await fetch(
                `${NetworkConfig.downloadFileVersion2}/${fileId}`,
                {
                    method: "GET",
                    headers: {
                        "Content-Type": "application/json",
                        Authorization: `${tokenType} ${accessToken}`,
                        "x-hasura-role": getRoleKey(role)
                    }
                }
            );

            if (downloadAssemblyProtocolFileResponse.status === 200) {
                downloadedAssemblyProtocolFile = downloadAssemblyProtocolFileResponse;
            } else {
                this.setProtocolFileId("");
                this.setProtocolFileURL("");
                console.error(
                    "Error occured while trying to download the assemblyProtocol file: ",
                    downloadAssemblyProtocolFileResponse
                );
            }
        } catch (error) {
            this.setProtocolFileId("");
            this.setProtocolFileURL("");
            console.error("Error saving assemblyProtocol: ", error);
        }
        return downloadedAssemblyProtocolFile;
    };

    getAssemblyProtocolFile = async (assemblyId: string) => {
        this.setIsProtocolPdfLoading(true);
        try {
            const { data }: { data: GetAssemblyProtocolFile } =
                await apolloClientInstance.query<GetAssemblyProtocolFile>({
                    query: GET_ASSEMBLY_PROTOCOL_FILE,
                    fetchPolicy: "no-cache",
                    variables: {
                        assemblyid: assemblyId
                    }
                });

            if (data.file_assemblyfiles && data.file_assemblyfiles.length > 0) {
                this.setProtocolFileId(data.file_assemblyfiles[0].id);

                const downloadedAssemblyProtocolFile = await this.downloadAssemblyProtocolFile(
                    data.file_assemblyfiles[0].fileid
                );

                if (downloadedAssemblyProtocolFile) {
                    let fileBlob = await downloadedAssemblyProtocolFile.blob();
                    const objUrl = window.URL.createObjectURL(fileBlob);

                    // Revoke old URL if it exists
                    if (this.protocolFileURL) {
                        window.URL.revokeObjectURL(this.protocolFileURL);
                    }

                    this.setProtocolFileURL(objUrl);
                }
            } else {
                this.setProtocolFileId("");
                this.setProtocolFileURL("");
            }
        } catch (error) {
            this.setProtocolFileId("");
            this.setProtocolFileURL("");
            console.error("Error loading assembly protocol file:", error);
        }

        this.setIsProtocolPdfLoading(false);
    };

    checkAndDownloadAssemblyProtocolPdf = async (assemblyId: string) => {
        this.setIsProtocolPDFDownloading(true);
        await this.checkIfAssemblyDataHasChanged(assemblyId || "");

        if (this.assemblyDataHasChanged || this.protocolFileId === "") {
            await this.generateAssemblyProtocolPdf(assemblyId);
            this.setAssemblyDataHasChanged(false);
        }

        if (this.protocolFileId !== "") {
            await this.downloadAssemblyProtocolPdf();
        }

        this.setIsProtocolPDFDownloading(false);
    };

    checkIfAssemblyDataHasChanged = async (assemblyId: string) => {
        try {
            const { data }: { data: GetGenerateNewProtocolData } =
                await apolloClientInstance.query<GetGenerateNewProtocolData>({
                    query: GET_GENERATE_NEW_PROTOCOL_DATA,
                    variables: { assemblyid: assemblyId },
                    fetchPolicy: "no-cache"
                });

            runInAction(() => {
                if (data.file_v_generate_new_protocol && data.file_v_generate_new_protocol.length > 0) {
                    this.setProtocolFileId(data.file_v_generate_new_protocol[0].fileid);
                    this.setAssemblyDataHasChanged(data.file_v_generate_new_protocol[0].generate_new_protocol || false);
                }
            });
        } catch (error) {
            console.error("Failed to fetch data from view file_v_generate_new_protocol:", error);
            throw error;
        }
    };

    generateAssemblyProtocolPdf = async (assemblyId: string) => {
        try {
            const accessToken = this.rootStore.authStore.token;
            const tokenType = this.rootStore.authStore.tokenType;
            const role = this.rootStore.authStore.user?.role;

            const response = await fetch(NetworkConfig.generateassemblyprotocolpdf + assemblyId, {
                method: "POST",
                headers: {
                    "Content-Type": "application/json",
                    Authorization: `${tokenType} ${accessToken}`,
                    "x-hasura-role": getRoleKey(role)
                }
            });

            if (response.status === 200) {
                const result = await response.json();

                this.setProtocolFileId(JSON.parse(result.data));
            } else {
                this.setProtocolFileId("");
                console.error("Error generateassemblyprotocolpdf: ", response);
                this.rootStore.uiStore.printStatusMessage(
                    i18next.t("screens.assemblies.protocolcontent.generate_error"),
                    MessageType.ERROR
                );
            }
        } catch (error) {
            this.setProtocolFileId("");
            console.error("Error while trying to call generateassemblyprotocolpdf: ", error);
            this.rootStore.uiStore.printStatusMessage(
                i18next.t("screens.assemblies.protocolcontent.generate_error") + error,
                MessageType.ERROR
            );
        }
    };

    downloadAssemblyProtocolPdf = async () => {
        const accessToken = this.rootStore.authStore.token;
        const tokenType = "Bearer";
        const role = this.rootStore.authStore.user?.role;

        try {
            const fetchResult = await fetch(`${NetworkConfig.downloadFileVersion2}/${this.protocolFileId}`, {
                method: "GET",
                headers: {
                    "Content-Type": "application/json",
                    Authorization: `${tokenType} ${accessToken}`,
                    "x-hasura-role": getRoleKey(role)
                }
            });

            if (fetchResult.status === 200) {
                try {
                    const assemblyDate = this.rootStore.assemblyStore.currentAssembly?.date;
                    const date = assemblyDate ? format(new Date(assemblyDate), "dd_MM_yyyy") : "";
                    const title = this.rootStore.assemblyStore.currentAssembly?.title?.replace(/\s+/g, "_") || "";
                    const fileName = `Protokoll_${title}_am_${date}`;
                    // Start download
                    const fileBlob = await fetchResult.blob();
                    const objUrl = window.URL.createObjectURL(fileBlob);
                    const a = document.createElement("a");
                    a.href = objUrl;
                    a.download = fileName;
                    document.body.appendChild(a);
                    a.click();
                    a.remove();
                } catch (error) {
                    this.rootStore.uiStore.printStatusMessage(
                        i18next.t("screens.realestate.assembly.error.assembly_file_download_error"),
                        MessageType.ERROR
                    );
                }
            } else {
                this.rootStore.uiStore.printStatusMessage(
                    i18next.t("screens.realestate.assembly.error.assembly_file_download_error"),
                    MessageType.ERROR
                );
            }
        } catch (fetchError) {
            this.rootStore.uiStore.printStatusMessage(
                i18next.t("screens.realestate.assembly.error.assembly_file_download_error"),
                MessageType.ERROR
            );
        }
    };

    /* SETTER */

    setInitialProtocol = () => {
        const currentAssembly = this.rootStore.assemblyStore.currentAssembly;
        if (currentAssembly) {
            this.initialAssemblyProtocol = {
                protocolauthor: currentAssembly.protocolauthor,
                protocoldate: currentAssembly.protocoldate
            };
        }
    };

    setAssemblyDataHasChanged = (assemblyDataHasChanged: boolean) => {
        this.assemblyDataHasChanged = assemblyDataHasChanged;
    };

    setIsProtocolPDFDownloading = (isProtocolPDFDownloading: boolean) => {
        this.isProtocolPDFDownloading = isProtocolPDFDownloading;
    };

    setProtocolFileId = (protocolFileId: string) => {
        this.protocolFileId = protocolFileId;
    };

    setProtocolFileURL = (protocolFileURL: string) => {
        this.protocolFileURL = protocolFileURL;
    };

    setIsProtocolPdfLoading = (isProtocolPdfLoading: boolean) => {
        this.isProtocolPdfLoading = isProtocolPdfLoading;
    };
}
